import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, Checkbox, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { MultiStepOrderModal } from '../../orders/MultiStepOrderModal';
import { useUser } from '../../../hooks/useUser';
import { isBrand, isRMN } from '../../../utils';
import { usePortalPath } from '../../../hooks/usePortalPath'
import axiosInstant from '../../../api';
import { brandChecklistEndpoint } from '../../../api/endpoints';

// One row of GET /accountmanager/brands/:id/checklist. The API decides which
// items apply (in display order) and whether the brand's data shows each done.
export interface ChecklistItem {
  id: string;
  done: boolean;
}

type ChecklistId = 'site-tag' | 'create-order' | 'create-product' | 'invite-team';

// Text and button label for each checklist id (the actions are below).
const ITEM_CONFIG: Record<ChecklistId, { text: string; button: string; brandButton?: string }> = {
  'site-tag': {
    text: 'Complete onboarding: Add Site Tag to your website',
    button: 'Add Site Tag',
  },
  'create-order': {
    text: 'Create your first Order',
    button: 'Add Order',
    brandButton: 'Request A Quote',
  },
  'create-product': {
    text: 'Create your first Product',
    button: 'Add Product',
  },
  'invite-team': {
    text: 'Invite your Team Members',
    button: 'Invite Team Members',
  },
};

// The dashboard keeps only the ids this build knows how to show.
export type KnownChecklistItem = ChecklistItem & { id: ChecklistId };
const isKnownChecklistItem = (item: any): item is KnownChecklistItem =>
  !!item && typeof item === 'object' && Object.prototype.hasOwnProperty.call(ITEM_CONFIG, item.id);

// What this browser keeps per brand: the user's own ticks (for items the
// brand's data doesn't show done, e.g. a one-person team), a dismissal, and
// the last list the API returned (shown straight away on the next visit, so
// the card doesn't pop in and reflow the grid).
interface StoredChecklist {
  checked: Partial<Record<ChecklistId, boolean>>;
  dismissed: boolean;
  items: KnownChecklistItem[] | null;
}

const storageKey = (brandId: number) => `tdc_todo_${brandId}`;
// The pre-TDC-1686 list kept its progress in two global keys; they seed a
// brand that has no entry of its own yet.
const LEGACY_PROGRESS_KEY = 'tdc_onboarding_progress';
const LEGACY_COMPLETED_KEY = 'tdc_onboarding_completed';

const readStored = (brandId: number): StoredChecklist => {
  const empty: StoredChecklist = { checked: {}, dismissed: false, items: null };
  if (!brandId) return empty;
  try {
    const raw = localStorage.getItem(storageKey(brandId));
    if (raw) {
      const parsed = JSON.parse(raw) || {};
      return {
        checked: parsed.checked && typeof parsed.checked === 'object' ? parsed.checked : {},
        dismissed: parsed.dismissed === true,
        items: Array.isArray(parsed.items) ? parsed.items.filter(isKnownChecklistItem) : null,
      };
    }
    const legacy = localStorage.getItem(LEGACY_PROGRESS_KEY);
    const legacyChecked = legacy ? JSON.parse(legacy) : null;
    return {
      checked: legacyChecked && typeof legacyChecked === 'object' ? legacyChecked : {},
      dismissed: localStorage.getItem(LEGACY_COMPLETED_KEY) === 'true',
      items: null,
    };
  } catch {
    return empty;
  }
};

const writeStored = (brandId: number, stored: StoredChecklist) => {
  if (!brandId) return;
  try {
    localStorage.setItem(storageKey(brandId), JSON.stringify(stored));
  } catch {
    // Storage blocked (private window): the list still works for this visit.
  }
};

type ChecklistState = StoredChecklist & {
  brandId: number;
  // The API request for this brand: still out, answered, or failed.
  status: 'loading' | 'ok' | 'error';
};

const initialState = (brandId: number): ChecklistState => ({ brandId, status: 'loading', ...readStored(brandId) });

// The dashboard To Do list for one brand: the API's verdicts, the user's own
// ticks and dismissal, and whether the card should show at all.
export function useDashboardChecklist(brandId: number) {
  const [state, setState] = useState<ChecklistState>(() => initialState(brandId));
  // Switching brands (a multi-brand user changing portals) starts from that
  // brand's stored entry.
  let current = state;
  if (state.brandId !== brandId) {
    current = initialState(brandId);
    setState(current);
  }

  useEffect(() => {
    writeStored(current.brandId, { checked: current.checked, dismissed: current.dismissed, items: current.items });
  }, [current.brandId, current.checked, current.dismissed, current.items]);

  // Only the newest request may update the list (a slow focus refetch must
  // not overwrite a newer result).
  const request = useRef(0);
  const refresh = useCallback(async () => {
    const id = ++request.current;
    if (!brandId) return;
    try {
      const response = await axiosInstant.get(brandChecklistEndpoint(brandId));
      if (id !== request.current) return;
      const raw = Array.isArray(response?.data?.items) ? response.data.items : [];
      const items: KnownChecklistItem[] = raw.filter(isKnownChecklistItem);
      setState((prev) => (prev.brandId === brandId ? { ...prev, items, status: 'ok' } : prev));
    } catch (error) {
      // Keep the stored list; with none, the card hides.
      console.error('Error loading the dashboard checklist:', error);
      if (id !== request.current) return;
      setState((prev) => (prev.brandId === brandId ? { ...prev, status: 'error' } : prev));
    }
  }, [brandId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // A product added or a team member invited elsewhere is picked up when the
  // user comes back to this tab.
  useEffect(() => {
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [refresh]);

  const toggle = useCallback((itemId: ChecklistId) => {
    setState((prev) => ({ ...prev, checked: { ...prev.checked, [itemId]: !prev.checked[itemId] } }));
  }, []);

  const dismiss = useCallback(() => {
    setState((prev) => ({ ...prev, dismissed: true }));
  }, []);

  // Hidden once dismissed, or once the brand's data shows everything done.
  // Before the first answer (no stored list) the card holds its slot.
  let show = false;
  if (brandId && !current.dismissed) {
    show = current.items ? current.items.some((item) => !item.done) : current.status === 'loading';
  }

  return { items: current.items, checked: current.checked, show, toggle, dismiss, refresh };
}

interface ToDoListProps {
  // null while the first request is out (nothing stored yet).
  items: KnownChecklistItem[] | null;
  checked: Partial<Record<ChecklistId, boolean>>;
  onToggle: (id: ChecklistId) => void;
  onDismiss: () => void;
  onOrderCreated?: () => void;
}

const ToDoList: React.FC<ToDoListProps> = ({ items, checked, onToggle, onDismiss, onOrderCreated }) => {
  const navigate = useNavigate();
  const { p } = usePortalPath();
  const { user, role } = useUser();
  const isBrandRole = isBrand(role) || isRMN(user);
  const [showOrderModal, setShowOrderModal] = useState(false);

  const actions: Record<ChecklistId, () => void> = {
    'site-tag': () => navigate(p('/settings/onboarding')),
    'create-order': () => setShowOrderModal(true),
    'create-product': () => {
      window.dispatchEvent(new CustomEvent('openAddProductModal'));
    },
    'invite-team': () => navigate(p('/settings/team?new=true')),
  };

  const rows = items || [];
  // Done by the brand's data, or ticked by the user.
  const isDone = (item: KnownChecklistItem) => item.done || !!checked[item.id];
  const allDone = rows.length > 0 && rows.every(isDone);

  return (
    <Box className="flex flex-col h-full min-h-0">
      {/* Header */}
      <Box className="flex items-center justify-between mb-4 flex-shrink-0">
        <Typography
          sx={{
            color: 'var(--text)',
            fontFamily: 'CircularStd',
            fontSize: { xs: '15px', md: '18px' },
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: 'normal',
          }}
        >
          To Do List
        </Typography>

        {allDone && (
          <Button
            onClick={onDismiss}
            sx={{
              backgroundColor: 'var(--accent)',
              color: 'var(--Base-0, #FFF)',
              fontFamily: 'CircularStd',
              fontSize: '12px',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '6px',
              padding: '6px 12px',
              '&:hover': {
                backgroundColor: '#8A6D41',
              },
            }}
          >
            Mark as Done
          </Button>
        )}
      </Box>

      <Divider sx={{ marginBottom: '16px', borderColor: 'var(--border)' }} />

      <Box className="flex-1 min-h-0 overflow-y-auto">
        <Box className="space-y-[10px]">
          {rows.map((item, index) => {
            const config = ITEM_CONFIG[item.id];
            const done = isDone(item);
            return (
              <Box key={item.id}>
                <Box className="flex items-center justify-between gap-2 py-3">
                  <Box className="flex items-center gap-3 flex-1 min-w-0 max-md:gap-2">
                    {/* Ticked by the brand's data (locked) or by the user. */}
                    <Checkbox
                      checked={done}
                      disabled={item.done}
                      onChange={() => onToggle(item.id)}
                      id={`todo-${item.id}`}
                      sx={{
                        color: 'var(--accent)',
                        padding: { xs: '4px', md: '9px' },
                        '&.Mui-checked, &.Mui-checked.Mui-disabled': {
                          color: 'var(--accent)',
                        },
                      }}
                    />
                    <label
                      htmlFor={`todo-${item.id}`}
                      className={`min-w-0 text-text-1 font-bold text-lg max-md:text-[13px] max-md:truncate ${
                        item.done ? '' : 'cursor-pointer'
                      } ${done ? 'opacity-20 line-through' : ''}`}
                    >
                      {config.text}
                    </label>
                  </Box>

                  {!done && (
                    <Button
                      onClick={actions[item.id]}
                      sx={{
                        border: '1px solid var(--accent)',
                        color: 'var(--accent-text)',
                        backgroundColor: 'var(--surface)',
                        fontFamily: 'CircularStd',
                        fontSize: { xs: '11px', md: '12px' },
                        fontWeight: 450,
                        textTransform: 'none',
                        borderRadius: '6px',
                        padding: { xs: '5px 10px', md: '6px 12px' },
                        minWidth: 'auto',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        '&:hover': {
                          backgroundColor: 'var(--tint)',
                          borderColor: '#8A6D41',
                        },
                      }}
                    >
                      {isBrandRole && config.brandButton ? config.brandButton : config.button}
                    </Button>
                  )}
                </Box>

                {index < rows.length - 1 && (
                  <Divider sx={{ borderColor: 'var(--border)' }} />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {showOrderModal && (
        <MultiStepOrderModal
          open={showOrderModal}
          onClose={() => setShowOrderModal(false)}
          onSuccess={onOrderCreated}
          mode="create"
        />
      )}
    </Box>
  );
};

export default ToDoList;
